// Lightweight auth context for the Book Store customer surface.
// Independent from `auth_context.js` (which handles The Loom Google OAuth).
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { bookstoreApi } from '@/lib/bookstoreApi';
import { useLocation } from 'react-router-dom';
import { needsBookstoreAuthProbe } from '@/lib/publicRoutes';

const Ctx = createContext({ user: null, checking: true, refresh: () => {}, logout: () => {} });

export function BookstoreAuthProvider({ children }) {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: u } = await bookstoreApi.me();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  const refreshOptional = useCallback(async () => {
    try {
      const response = await bookstoreApi.optionalMe();
      setUser(response?.user || null);
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await bookstoreApi.logout(); } catch (err) {
      // Cookie clear is best-effort — the server may already have invalidated
      // the session. We still clear local state below.
      console.debug('[bookstore-auth/logout] server call failed:', err);
    }
    setUser(null);
  }, []);

  useEffect(() => {
    if (!needsBookstoreAuthProbe(location.pathname)) {
      setChecking(false);
      return;
    }
    setChecking(true);
    refreshOptional();
  }, [refreshOptional, location.pathname]);

  return (
    <Ctx.Provider value={{ user, checking, refresh, logout, setUser }}>
      {children}
    </Ctx.Provider>
  );
}

export function useBookstoreAuth() {
  return useContext(Ctx);
}
